import type { FilterItem, FilterColumn } from '#shared/types/filters'
import * as LZString from 'lz-string'

// Create refs outside the composable to share state
const filterQuery = ref('')
const currentFilter = ref<FilterItem>({
  filterColumn: '',
  filterOperator: 'equals',
  filterValue: '',
  step: 0
})
const activeFilters = ref<FilterItem[]>([])

const emptyFilter: FilterItem = {
  filterColumn: '',
  filterOperator: 'equals',
  filterValue: '',
  step: 0
}

export function useFilters() {
  const route = useRoute()
  const router = useRouter()

  // Parse from query instead of hash
  if (route.query.f) {
    try {
      const filtersStr = LZString.decompressFromEncodedURIComponent(route.query.f as string)
      activeFilters.value = JSON.parse(filtersStr)
    } catch (e) {
      console.error('Failed to parse filters from URL:', e)
    }
  }

  // Update filterQuery when activeFilters changes
  watch(
    activeFilters,
    newFilters => {
      filterQuery.value = JSON.stringify(newFilters)
    },
    { deep: true }
  )

  // Functions that modify shared state
  function addCurrentFilter(filter: FilterItem) {
    activeFilters.value.push(filter)
    currentFilter.value = emptyFilter
  }

  function removeFilter(index: number) {
    activeFilters.value.splice(index, 1)
    activeFilters.value = activeFilters.value.map((filter, idx) => ({
      ...filter,
      step: idx
    }))

    // Update URL when filters change
    const query = { ...route.query }

    if (activeFilters.value.length === 0) {
      delete query.f
      if (!query.c) delete query.c
    } else {
      query.f = LZString.compressToEncodedURIComponent(JSON.stringify(activeFilters.value))
    }

    router.replace({ query })
  }

  function clearFilters() {
    activeFilters.value = []
    currentFilter.value = emptyFilter
    filterQuery.value = ''
  }

  function loadFilterForEditing(filter: FilterItem) {
    const index = activeFilters.value.findIndex(
      f =>
        f.filterColumn === filter.filterColumn &&
        f.filterOperator === filter.filterOperator &&
        f.filterValue === filter.filterValue
    )

    if (index !== -1) {
      activeFilters.value.splice(index, 1)
    }

    currentFilter.value = filter
  }

  function copyShareableUrl(columns?: string[]) {
    const query = Object.fromEntries(
      Object.entries(route.query).map(([key, value]) => [key, String(value)])
    )

    if (activeFilters.value.length) {
      query.f = LZString.compressToEncodedURIComponent(JSON.stringify(activeFilters.value))
    }

    if (columns?.length) {
      query.c = LZString.compressToEncodedURIComponent(columns.join(','))
    }

    const url = new URL(window.location.href)
    url.search = new URLSearchParams(query).toString()

    navigator.clipboard.writeText(url.toString())

    const toast = useToast()
    toast.add({
      title: 'URL Copied',
      description: 'Shareable URL has been copied to clipboard',
      color: 'green'
    })
  }

  function getColumnsFromState(allColumns: FilterColumn[], defaultColumns: FilterColumn[]) {
    // First try URL query param
    const columnsParam = route.query.c
    if (columnsParam) {
      try {
        const columnsStr = LZString.decompressFromEncodedURIComponent(columnsParam as string)
        const urlColumns = columnsStr.split(',')
        return allColumns.filter(col => urlColumns.includes(col.key))
      } catch (e) {
        console.error('Failed to parse columns from URL:', e)
      }
    }

    // TODO: Check user preferences when implemented
    // const userPreferences = useUserPreferences()
    // if (userPreferences.value?.columns) {
    //   return userPreferences.value.columns
    // }

    // Fallback to defaults
    return defaultColumns.slice()
  }

  const parseCompressedFilters = (compressedStr: string) => {
    try {
      const decompressed = LZString.decompressFromEncodedURIComponent(compressedStr)
      return JSON.parse(decompressed)
    } catch (error) {
      console.error('[useFilters] Failed to parse compressed filters:', error)
      return null
    }
  }

  const compressFilters = (filters: FilterItem[]) => {
    try {
      const filterStr = JSON.stringify(filters)
      return LZString.compressToEncodedURIComponent(filterStr)
    } catch (error) {
      console.error('[useFilters] Failed to compress filters:', error)
      return null
    }
  }

  // Add function to get current filters URL state
  function getCurrentFilterState(): string {
    const url = new URL(window.location.href)
    return url.hash
  }

  return {
    filterQuery,
    currentFilter,
    activeFilters,
    addCurrentFilter,
    removeFilter,
    clearFilters,
    loadFilterForEditing,
    copyShareableUrl,
    getColumnsFromState,
    parseCompressedFilters,
    compressFilters,
    getCurrentFilterState
  }
}
